import React, { useState, useEffect, useRef } from 'react';
import { Table, Row, Col, Breadcrumb, Button, Typography, notification, Space, Tooltip, Empty, Grid } from 'antd';
import { CSVLink } from 'react-csv';
import { DownloadOutlined, PictureOutlined } from '@ant-design/icons';
import moment from 'moment';
import { intialPaginationState, showingPageTotalRange, unexpectedError, onlyYears, filter, PictureCell } from '../../../common_functions';

const { Text } = Typography;

// This app's actual brand tokens (see variables.less: @primary-color, @table-text, button/table colors) —
// used instead of antd's stock saturated tag colors so this report matches the rest of the gallery UI.
const BRAND = {
  gold: '#a6a300',
  goldTint: { bg: '#f7f7e8', border: '#d9d68f', text: '#7a7800' },
  ink: '#212329',
  text: '#595959',
  textMuted: '#8c8c8c',
  border: '#e5e5e5',
};

// A small set of muted, low-saturation tones (not antd's saturated presets) so multiple distinct
// collections stay visually distinguishable without reading as a rainbow of confetti tags.
const SOFT_PALETTE = [
  BRAND.goldTint,
  { bg: '#eef1f4', border: '#c3ccd6', text: '#51606f' }, // slate
  { bg: '#eef4ee', border: '#bcd4bc', text: '#4a6b4a' }, // sage
  { bg: '#f5efe9', border: '#ddc8b3', text: '#8a6a48' }, // taupe
  { bg: '#f3ecee', border: '#d8bfc6', text: '#8a5060' }, // dusty rose
  { bg: '#eef2f2', border: '#bcd4d4', text: '#4a6b6b' }, // teal
];

const softColorForText = text => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return SOFT_PALETTE[Math.abs(hash) % SOFT_PALETTE.length];
};

const SoftTag = ({ tone = BRAND.goldTint, children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '1px 8px',
      fontSize: 12,
      lineHeight: '20px',
      borderRadius: 4,
      background: tone.bg,
      border: `1px solid ${tone.border}`,
      color: tone.text,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      maxWidth: '100%',
    }}
  >
    {children}
  </span>
);

const CLASSIFICATION_FIELDS = [
  ['catalog_level', 'Catalog Level'],
  ['record_type', 'Record Type'],
  ['classification', 'Classification'],
  ['obj_type', 'Object Type'],
  ['work_type', 'Work Type, Output'],
];

// Each image_description sub-field, used to build the expanded-row detail panel.
// type 'text' reads description[key] directly; type 'tags' reads description.tagGroups[key] (an array).
const DESCRIPTION_FIELDS = [
  { key: 'imageDescription', label: 'Image Description', type: 'text' },
  { key: 'specificSub', label: 'Specific Subject', type: 'text' },
  { key: 'depictedLoc', label: 'Depicted Location', type: 'text' },
  { key: 'park', label: 'Park', type: 'text' },
  { key: 'subject_general', label: 'Subject General', type: 'tags' },
  { key: 'subject_general_landscape', label: 'Subject, General, Landscape', type: 'tags' },
  { key: 'season', label: 'Season', type: 'tags' },
  { key: 'landforms', label: 'Landforms', type: 'tags' },
  { key: 'bodies_of_water', label: 'Bodies of Water', type: 'tags' },
  { key: 'flora', label: 'Flora / Vegetation', type: 'tags' },
  { key: 'atmospheric', label: 'Atmospheric / Phenomenon', type: 'tags' },
  { key: 'human_and_animals', label: 'Human and Animals', type: 'tags' },
  { key: 'env_transportation', label: 'Built Environment - Transportation', type: 'tags' },
  { key: 'env_structure', label: 'Built Environment - Structures', type: 'tags' },
  { key: 'env_site', label: 'Built Environment - Site', type: 'tags' },
  { key: 'prominentFeatures', label: 'Prominent Natural Features', type: 'tags', accent: true },
];

const FIELDS = [
  'id',
  'acquisition',
  'collections.name',
  'catalog_level.name',
  'record_type.name',
  'classification.name',
  'obj_type.name',
  'work_type.name',
  'negative.obj_name',
  'negative.thumbnail',
  'negative.creation_earliest',
  'negative.creation_latest',
  'negative.negative_id',
  'negative.sheet_dimensions.label',
  'image_description.specific_sub',
  'image_description.depicted_loc',
  'image_description.image_description',
  'image_description.support_table_data.support_table_id.name',
  'image_description.support_table_data.support_table_id.fields',
  'image_description.geographic_location.geographic_locations_id.prominent_natural_features',
  'image_description.geographic_location.geographic_locations_id.park.park',
];

const buildClassifications = item =>
  CLASSIFICATION_FIELDS.map(([key, label]) => ({ label, value: item[key]?.name })).filter(c => c.value);

const buildDescription = item => {
  const imgDesc = item.image_description?.[0] || {};
  const tagGroups = (imgDesc.support_table_data || []).reduce((acc, row) => {
    const key = row?.support_table_id?.fields;
    const name = row?.support_table_id?.name;
    if (key && name) (acc[key] = acc[key] || []).push(name);
    return acc;
  }, {});
  const geoLocations = imgDesc.geographic_location || [];
  tagGroups.prominentFeatures = geoLocations.map(g => g?.geographic_locations_id?.prominent_natural_features).filter(Boolean);

  return {
    specificSub: imgDesc.specific_sub,
    depictedLoc: imgDesc.depicted_loc,
    imageDescription: imgDesc.image_description,
    park: geoLocations.find(g => g?.geographic_locations_id?.park?.park)?.geographic_locations_id?.park?.park,
    tagGroups,
  };
};

const descriptionPreview = description => description.imageDescription || description.specificSub || description.depictedLoc || '';

const descriptionCategoryCount = description => Object.values(description.tagGroups).filter(v => v.length).length;

const hasDescriptionDetails = description => !!(descriptionPreview(description) || description.park || descriptionCategoryCount(description));

const formatDescriptionText = description => {
  const lines = [];
  if (description.specificSub) lines.push(`Specific Subject: ${description.specificSub}`);
  if (description.depictedLoc) lines.push(`Depicted Location: ${description.depictedLoc}`);
  if (description.imageDescription) lines.push(`Image Description: ${description.imageDescription}`);
  if (description.park) lines.push(`Park: ${description.park}`);
  DESCRIPTION_FIELDS.filter(f => f.type === 'tags').forEach(({ key, label }) => {
    if (description.tagGroups[key]?.length) lines.push(`${label}: ${description.tagGroups[key].join(', ')}`);
  });
  return lines.join('\n');
};

const transformReportData = rawData =>
  rawData.map(item => ({
    id: item.id,
    acquisition: item.acquisition,
    collections: item.collections?.name,
    image_title: item.negative?.obj_name,
    thumbnail: item.negative?.thumbnail,
    date_range: onlyYears(item.negative?.creation_earliest, item.negative?.creation_latest),
    negative_number: item.negative?.negative_id,
    negative_size: item.negative?.sheet_dimensions?.label,
    classifications: buildClassifications(item),
    description: buildDescription(item),
  }));

const transformCsvData = rawData =>
  rawData.map(item => {
    const classifications = buildClassifications(item);
    const description = buildDescription(item);
    const row = {
      id: item.id,
      acquisition: item.acquisition,
      collections: item.collections?.name,
      image_title: item.negative?.obj_name,
      date_range: onlyYears(item.negative?.creation_earliest, item.negative?.creation_latest),
      negative_number: item.negative?.negative_id || '',
      negative_size: item.negative?.sheet_dimensions?.label || '',
    };

    CLASSIFICATION_FIELDS.forEach(([key, label]) => {
      row[`class_${key}`] = item[key]?.name || '';
    });

    DESCRIPTION_FIELDS.forEach(field => {
      row[`desc_${field.key}`] = field.type === 'text' ? description[field.key] || '' : (description.tagGroups[field.key] || []).join(', ');
    });

    return row;
  });

const CSV_HEADERS = [
  { label: 'ID', key: 'id' },
  { label: 'Acquisition', key: 'acquisition' },
  { label: 'Collection', key: 'collections' },
  { label: 'Image Title', key: 'image_title' },
  { label: 'Date Range', key: 'date_range' },
  { label: 'Negative Number', key: 'negative_number' },
  { label: 'Negative Size', key: 'negative_size' },
  ...CLASSIFICATION_FIELDS.map(([key, label]) => ({ label, key: `class_${key}` })),
  ...DESCRIPTION_FIELDS.map(field => ({ label: field.label, key: `desc_${field.key}` })),
];

const EMPTY_DASH = <span style={{ color: '#d9d9d9' }}>-</span>;

const ExpandedDescription = ({ description }) => {
  const textItems = [
    ['Image Description', description.imageDescription],
    ['Specific Subject', description.specificSub],
    ['Depicted Location', description.depictedLoc],
    ['Park', description.park],
  ].filter(([, value]) => value);
  const tagCategories = DESCRIPTION_FIELDS.filter(f => f.type === 'tags' && description.tagGroups[f.key]?.length);

  return (
    <div style={{ background: '#faf9f2', padding: 20, borderTop: `1px solid ${BRAND.border}` }}>
      <Row gutter={[32, 20]}>
        {textItems.length > 0 && (
          <Col xs={24} md={8} style={{ minWidth: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 10, color: BRAND.ink }}>
              Details
            </Text>
            {textItems.map(([label, value]) => (
              <div key={label} style={{ marginBottom: 12 }}>
                <Text style={{ fontSize: 11, fontWeight: 600, color: BRAND.textMuted, display: 'block', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {label}
                </Text>
                <Text style={{ wordBreak: 'break-word', color: BRAND.text }}>{value}</Text>
              </div>
            ))}
          </Col>
        )}
        {tagCategories.length > 0 && (
          <Col xs={24} md={16} style={{ minWidth: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 10, color: BRAND.ink }}>
              Categories
            </Text>
            <Row gutter={[16, 14]}>
              {tagCategories.map(field => (
                <Col xs={24} sm={12} lg={8} key={field.key} style={{ minWidth: 0 }}>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: BRAND.textMuted,
                      display: 'block',
                      marginBottom: 5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {field.label}
                  </Text>
                  <Space wrap size={[6, 6]} style={{ width: '100%' }}>
                    {description.tagGroups[field.key].map(name => (
                      <SoftTag key={name} tone={field.accent ? SOFT_PALETTE[2] : SOFT_PALETTE[1]}>
                        {name}
                      </SoftTag>
                    ))}
                  </Space>
                </Col>
              ))}
            </Row>
          </Col>
        )}
      </Row>
    </div>
  );
};

const ReportByNegative = props => {
  const csvLink = useRef(null);
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [acquisitionFilter, setAcquisitionFilter] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const todayDateTime = moment().format('MM-DD-YYYY-HH-mm-ss');
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const buildFilters = acquisition => {
    const isAdmin = props.roleType === 'admin';
    let filters = { object_type: { _eq: 'negative' } };

    if (!isAdmin) {
      const canViewNegatives = !!props.tabs_permissions?.negatives?.view;
      filters = { ...filters, collections: { _in: canViewNegatives ? props.collections_permissions.view : [] } };
    }

    if (acquisition) {
      filters = { ...filters, acquisition: { _icontains: acquisition } };
    }

    return filters;
  };

  const getNegativeReports = async (pagination, acquisition, sorter = null) => {
    try {
      setLoading(true);
      const response = await props.getItems('collection_obj', {
        fields: FIELDS,
        filters: buildFilters(acquisition),
        sort: sorter?.field ? `${sorter.order === 'ascend' ? '' : '-'}${sorter.field}` : '-id',
        offset: pagination.pageSize * (pagination.current - 1),
        limit: pagination.pageSize,
        meta: 'filter_count',
      });

      setReports(transformReportData(response.data));
      pagination.total = response.meta.filter_count;
      setPagination(pagination);
      setExpandedRowKeys([]);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNegativeReports(intialPaginationState, null);
  }, []);

  const handleTableChange = async (pagination, filters, sorter) => {
    const acquisition = filters.acquisition?.[0] || null;
    setAcquisitionFilter(acquisition);
    await getNegativeReports(pagination, acquisition, sorter);
  };

  const handleDownloadCSV = async () => {
    try {
      setLoading(true);
      const response = await props.getItems('collection_obj', {
        fields: FIELDS,
        filters: buildFilters(acquisitionFilter),
        sort: '-id',
        limit: -1,
      });

      if (!response.data?.length) {
        notification.info({ message: 'No data available for download', duration: 2 });
        return;
      }

      setCsvData(transformCsvData(response.data));
      setTimeout(() => csvLink.current?.link.click(), 0);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: true,
      responsive: ['md'],
    },
    {
      title: 'Acquisition',
      dataIndex: 'acquisition',
      key: 'acquisition',
      width: 150,
      render: text => <Text strong>{text || EMPTY_DASH}</Text>,
      ...filter('acquisition'),
    },
    {
      title: 'Collection',
      dataIndex: 'collections',
      key: 'collections',
      width: 170,
      responsive: ['md'],
      render: text => (text ? <SoftTag tone={softColorForText(text)}>{text}</SoftTag> : EMPTY_DASH),
    },
    {
      title: 'Image Title',
      dataIndex: 'image_title',
      key: 'image_title',
      width: 190,
      ellipsis: { showTitle: false },
      render: text =>
        text ? (
          <Tooltip title={text} placement="topLeft">
            <span>{text}</span>
          </Tooltip>
        ) : (
          EMPTY_DASH
        ),
    },
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      align: 'center',
      width: 110,
      render: text =>
        text ? (
          <div
            style={{
              width: 58,
              height: 58,
              margin: '0 auto',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PictureCell dataIndexValue={text} getFile={props.getFile} width={58} isMultiImage />
          </div>
        ) : (
          <div
            style={{
              width: 58,
              height: 58,
              margin: '0 auto',
              borderRadius: 8,
              background: '#fafafa',
              border: '1px dashed #e8e8e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#d9d9d9',
            }}
          >
            <PictureOutlined />
          </div>
        ),
    },
    {
      title: 'Date Range',
      dataIndex: 'date_range',
      key: 'date_range',
      width: 120,
      responsive: ['sm'],
      render: text => text || EMPTY_DASH,
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Negative Number</span>,
      dataIndex: 'negative_number',
      key: 'negative_number',
      width: 170,
      responsive: ['md'],
      render: text => text || EMPTY_DASH,
    },
    {
      title: <span style={{ whiteSpace: 'nowrap' }}>Negative Size</span>,
      dataIndex: 'negative_size',
      key: 'negative_size',
      width: 140,
      responsive: ['md'],
      render: text => text || EMPTY_DASH,
    },
    ...CLASSIFICATION_FIELDS.map(([key, label]) => ({
      title: label,
      dataIndex: 'classifications',
      key: `classification_${key}`,
      width: 150,
      responsive: ['lg'],
      render: classifications => {
        const match = classifications.find(c => c.label === label);
        return match ? <SoftTag>{match.value}</SoftTag> : EMPTY_DASH;
      },
    })),
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 260,
      render: (description, record) => {
        const preview = descriptionPreview(description);
        const categoryCount = descriptionCategoryCount(description);
        const expanded = expandedRowKeys.includes(record.id);
        return (
          <div>
            <Text ellipsis={{ tooltip: preview }} style={{ display: 'block', maxWidth: 230 }}>
              {preview || EMPTY_DASH}
            </Text>
            {hasDescriptionDetails(description) && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() => setExpandedRowKeys(expanded ? [] : [record.id])}
              >
                {expanded ? 'Hide details' : `View details${categoryCount ? ` (${categoryCount})` : ''}`}
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Row className={loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/report-by-negative" }, { title: "Negative Report" }]} />
      </Col>
      <Col span={24} className="white-paper">
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={24} md={12}>
              <Space align="center" size={10}>
                <PictureOutlined style={{ fontSize: 20, color: BRAND.gold }} />
                <div>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Negative Report
                  </Typography.Title>
                  <Text type="secondary">{pagination.total ? `${pagination.total} negative objects` : ''}</Text>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? 12 : 0 }}>
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                loading={loading}
                onClick={handleDownloadCSV}
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Download CSV
              </Button>
              <CSVLink
                ref={csvLink}
                data={csvData}
                headers={CSV_HEADERS}
                filename={`negative_report_${todayDateTime}.csv`}
                style={{ display: 'none' }}
              ></CSVLink>
            </Col>
          </Row>
          <hr style={{ margin: '16px 0' }} />
          <div className="negative-report-table">
            <Table
              className="no-fixed-last-col"
              scroll={{ x: 'max-content' }}
              tableLayout="fixed"
              sticky
              size={isMobile ? 'small' : 'middle'}
              bordered
              columns={columns}
              rowKey={record => record.id}
              dataSource={reports}
              rowClassName={(_, index) => (index % 2 === 1 ? 'negative-report-row-alt' : '')}
              pagination={
                reports.length > 0
                  ? { ...pagination, ...showingPageTotalRange, simple: isMobile, showSizeChanger: !isMobile }
                  : false
              }
              loading={loading}
              onChange={handleTableChange}
              locale={{ emptyText: <Empty description="No negative objects found" /> }}
              expandable={{
                expandedRowKeys,
                onExpand: (expanded, record) => setExpandedRowKeys(expanded ? [record.id] : []),
                expandedRowRender: record => <ExpandedDescription description={record.description} />,
                expandIcon: () => null,
                showExpandColumn: false,
                rowExpandable: record => hasDescriptionDetails(record.description),
              }}
            />
          </div>
          <style>{`
            .negative-report-table .ant-table-tbody > tr.negative-report-row-alt > td { background: #faf9f2 !important; }
            .negative-report-table .ant-table-thead > tr > th { border-bottom: 2px solid ${BRAND.gold}; }
            .negative-report-table .ant-table-tbody > tr:hover > td { background: #f7f7e8 !important; }
          `}</style>
        </div>
      </Col>
    </Row>
  );
};

export default ReportByNegative;
